import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import withAppProviders from "./withAppProviders";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import useAuthentication from "./hooks/useAuthentication";
import PageLoading from "./components/shared/pageLoading";
import Footer from "./components/footer/footer";
import { useDispatch } from "react-redux";
import { AppDispatch } from "./store/store";
import { fetchWarehouseDetails } from "./store/slices/warehouseSlice";
import ScrollTop from "./utils/scrollTop";
import HeaderWarning from "./components/header/headerWarning";
import { Box, Typography } from "@mui/material";
import WarehouseBanner from "./components/header/warehouseBanner";
import MainMenu from "./components/navbar/mainMenu";
import BuilderIOPage from "./builderio/components/builderIOPage";
import "./builderio/builder-registry";
import FinancingSolutions from "./pages/impact-programs/FinancingSolutions";
import ImpactPrograms from "./pages/impact-programs/ImpactPrograms";
import LansingMarketingServices from "./pages/impact-programs/LansingMarketingServices";
import LeadGeneration from "./pages/impact-programs/LeadGeneration";
import LoyaltyLink from "./pages/impact-programs/LoyaltyLink";
import { useSelector } from "react-redux";
import { RootState } from "./store/store";
import ImpactRewards from "./pages/impact-programs/ImpactRewards";
import "../assets/css/fonts.css";
import ProductGallery from "./pages/productGallery/productGallery";
import Catalog from "./pages/catalog/catalog";
import Product from "./pages/product/product";
import Homepage from "./pages/home/homepage";
import "../assets/css/App.css";
import "../assets/css/AppExtensions.css";
import { useMediaQuery, useTheme } from "@mui/material";
import KioskManager from "./utils/kioskManager";
import PingdomComponent from "./components/shared/pingdomComponent";
import RenaissanceProduct from "./pages/requestQuote/renaissanceProducts";
import Clearwin from "./components/customerPrograms/clearwin";
import CustomWindowsQuote from "./pages/requestQuote/customWindowsQuote";
import GlobalNotification from "./components/header/GlobalNotifications";
import CartPage from "./pages/cart/cartPage";
import Checkout from "./pages/checkout/checkout";
import LockIcon from "@mui/icons-material/Lock";
import lansingnowReverseImg from "../img/lansingnow_horz-logo-reverse-rgb.svg";
import OrderCompleted from "./pages/checkout/orderPlaced/orderCompleted";
import OrderHistoryPage from "./pages/order/OrderHistoryPage";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import OrderDetailsPage from "./pages/order/OrderDetailsPage";
import ScrollToTopButton from "./components/shared/scrollToTopButton";
import { AppGAInitializer } from "./components/shared/AppGAInitializer";
import DQDashboard from "./pages/account/dqDashboard/dqDashboard";
import ContactUsPage from "./pages/contact-us/contactUs";
import OrderNotificationsPage from "./pages/account/orderNotifications/orderNotification";
import UserProfile from "./pages/account/userProfile";
import CustomerAccount from "./pages/account/customerAccount";
import MarketingContactUs from "./pages/contact-us/marketingContactUs";
import { ManageUsers } from "./pages/account/manageUsers";
import ResetPassword from "./pages/account/resetPassword";
import ReturnOrderPage from "./components/order/ReturnOrder/ReturnOrderPage";
import ReturnItemsDetailsPage from "./components/order/ReturnOrder/returnItemDetails";
import FAQPage from "./pages/transactional-pages/FAQ";
import ReturnPolicy from "./pages/transactional-pages/ReturnPolicy";
import ReturnItemsConfirmation from "./components/order/ReturnOrder/ReturnItemsConfirmation";
import useFavicon from "./hooks/useFavicon";
import ProgramsSavr from "./pages/impact-programs/Loyalty/ProgramsSavr";
import OnlineBillPay from "./pages/impact-programs/OnlineBillPay";
import ProgramImpactRewards from "./pages/impact-programs/programImpactRewards/ProgramImpactRewards";
import BuilderIOSymbolPage from "./builderio/components/builderIOSymbolPage";

const queryClient = new QueryClient();

const App: React.FC = () => {
  useFavicon();

  // Idle user logout
  KioskManager(() => {
    window.location.href = process.env.REACT_APP_WEB_URL + "/forceLogout";
  }, 1);

  // Top of the page listener
  window.addEventListener("scroll", () => {
    setTimeout(() => {
      if (window.scrollY === 0) document.body.classList.remove("notAtPageTop");
      else document.body.classList.add("notAtPageTop");
    }, 100);
  });

  // Floating mobile header
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [disableShipTo, setDisableShipTo] = useState(false);

  const { isWarehouseInfoLoaded } = useSelector(
    (state: RootState) => state.warehouseState
  );

  const headerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = headerRef.current;
    if (!isMobile) {
      document.documentElement.style.removeProperty("--mobile-header-height");
      return;
    }
    if (!element) return;
    const updateHeight = () => {
      const height = element.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        "--mobile-header-height",
        `${height}px`
      );
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isMobile, isWarehouseInfoLoaded]);

  const { isAuthenticated, isAuthenticating } = useAuthentication();
  const impersonatedCustomer = useSelector(
    (state: RootState) => state.userState.user.impersonatedCustomer
  );
  var isInternal = useSelector(
    (state: RootState) => state.userState.user.isInternal
  );

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!isAuthenticating) {
      dispatch(fetchWarehouseDetails(null));
    }
  }, [dispatch, isAuthenticating]);

  const { warehouseCode } = useSelector(
    (state: RootState) => state.warehouseState.selectedWarehouse
  );

  if (isAuthenticating || isWarehouseInfoLoaded === false) {
    return <PageLoading />;
  }

  const isCheckout = window.location.pathname == "/lbpcustomer/checkout";

  let basename = process.env.REACT_APP_SUBDIRECTORY;
  if (isAuthenticated) {
    basename = process.env.REACT_APP_STOREFRONT_SUBDIRECTORY;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={"/" + basename}>
        <AppGAInitializer />
        <PingdomComponent />
        <ScrollTop />

        <Box ref={headerRef} sx={boxStyles} className="fullHeaderBanner">
          <HeaderWarning />
          {isCheckout ? (
            <>
              <Box
                className="page-title"
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: { xs: "center", sm: "space-between" },
                  alignItems: "center",
                  gap: { xs: 1.5, sm: 0 },
                  px: 2,
                  py: { xs: 1.5, sm: 2 },
                  height: { xs: "auto", sm: "80px" },
                  bgcolor: "#62B5E5",
                  marginBottom: "10px",
                }}
              >
                <Box>
                  <img
                    src={lansingnowReverseImg}
                    alt="Lansing Now"
                    style={{ height: 47, maxWidth: "100%", display: "block" }}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography className="secure-text">
                    Secure Checkout
                  </Typography>
                  <LockIcon sx={{ color: "white", marginRight: "30px" }} />
                </Box>
              </Box>
            </>
          ) : (
            <>
              {(isInternal || impersonatedCustomer) && (
                <div className="headerImpersonatingMessage">
                  {isInternal && (
                    <a
                      href={process.env.REACT_APP_WEB_URL + "/Admin"}
                      style={{ textDecoration: "none", color: "white" }}
                    >
                      Administration
                    </a>
                  )}
                  {impersonatedCustomer && (
                    <>
                      <span
                        style={{
                          textDecoration: "none",
                          color: "white",
                          marginRight: "20px",
                          cursor: "default",
                        }}
                      >
                        Impersonating {impersonatedCustomer}
                      </span>
                      <a
                        href={process.env.REACT_APP_WEB_URL + "/logout"}
                        title="Click here to Logout."
                        style={{ textDecoration: "none", color: "white" }}
                      >
                        log out
                      </a>
                    </>
                  )}
                </div>
              )}
              {warehouseCode != null && (
                <WarehouseBanner disableShipTo={disableShipTo} />
              )}

              <MainMenu />
            </>
          )}
          <GlobalNotification />
        </Box>
        <div
          className="page-margin"
          style={
            isMobile ? { marginTop: "var(--mobile-header-height, 0px)" } : {}
          }
        >
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route
              path="/impact-programs/impact-programs-overview"
              element={<ImpactPrograms />}
            />
            <Route
              path="/impact-programs/financing-solutions"
              element={<FinancingSolutions />}
            />
            <Route
              path="/impact-programs/impact-rewards"
              element={<ImpactRewards />}
            />
            <Route
              path="/impact-programs/lansing-marketing-services"
              element={<LansingMarketingServices />}
            />
            <Route
              path="/impact-programs/lead-generation"
              element={<LeadGeneration />}
            />
            <Route
              path="/impact-programs/loyalty-link"
              element={<LoyaltyLink />}
            />
            <Route
              path="/gallery"
              element={
                <ProductGallery
                  warehouseCode={warehouseCode}
                  isAuthenticated={isAuthenticated}
                />
              }
            />
            <Route path="/online-bill-pay" element={<OnlineBillPay />} />
            <Route
              path="/renaissance-product"
              element={<RenaissanceProduct />}
            />
            <Route
              path="/windows-patio-doors-info"
              element={<CustomWindowsQuote />}
            />
            <Route path="/catalog" element={<Catalog />} />
            <Route
              path="/product"
              element={<Product warehouseCode={warehouseCode} />}
            />
            <Route path="/programs/clearwin" element={<Clearwin />} />
            <Route
              path="/cart"
              element={<CartPage setDisableShipTo={setDisableShipTo} />}
            />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/checkout/completed" element={<OrderCompleted />} />
            <Route path="/dqdashboard" element={<DQDashboard />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/info" element={<CustomerAccount />} />
            <Route path="/users" element={<ManageUsers />} />
            <Route path="/contact-us" element={<ContactUsPage />} />
            <Route
              path="/marketing-contact-us"
              element={<MarketingContactUs isAuthenticated={isAuthenticated} />}
            />
            <Route
              path="/ordernotifications"
              element={<OrderNotificationsPage />}
            />

            <Route path="/faq" element={<FAQPage />} />

            <Route path="/return-policy" element={<ReturnPolicy />} />
            {/* <Route path="/password-reset" element={<ResetPassword />} /> */}
            <Route path="*" element={<BuilderIOPage />} />
            <Route path="/order/history" element={<OrderHistoryPage />} />
            <Route path="/order/details/:id" element={<OrderDetailsPage />} />
            <Route path="/order/ReturnItems" element={<ReturnOrderPage />} />
            <Route path="/programs/savr/" element={<ProgramsSavr />} />
            <Route
              path="/order/returnItemsdetails"
              element={<ReturnItemsDetailsPage />}
            />
            <Route
              path="/programs/impactrewards"
              element={<ProgramImpactRewards />}
            />
            <Route
              path="/order/ReturnItemsConfirmation"
              element={<ReturnItemsConfirmation />}
            />

            {/* http://localhost:3000/visitor/symbol-preview?page=/visitor/lansing-branches/florida/sarasota-fl */}
            <Route path="/symbol-preview" element={<BuilderIOSymbolPage />} />
          </Routes>
        </div>
        <ScrollToTopButton />
        <Footer />
      </BrowserRouter>
    </QueryClientProvider>
  );
};

const boxStyles = {
  width: "100%",
  bgcolor: "white",
  "@media (max-width: 1199px)": {
    position: "fixed",
    top: 0,
    zIndex: 1000,
  },
};

const AppWithProviders = withAppProviders(App);

export default AppWithProviders;
